import { useDynamicLocale } from '@/hoslog-intl';
import {
  Stepper as Root,
  StepperDescription,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from 'ui/steper';

interface StepItem {
  step: number;
  title: string;
  description: string;
}
type EnsureReadonlyTuple<T> = T extends readonly [StepItem, ...StepItem[]]
  ? T
  : "❌ ERROR: 'items' must be a readonly array! Use 'as const'.";
type StepperProps<T extends readonly StepItem[]> = {
  items: EnsureReadonlyTuple<T>;
  defaultActive?: T[number]['step'];
  onValueChange?: (step: number) => void;
  value?: number;
};

export function Stepper<T extends readonly StepItem[]>({
  items,
  defaultActive,
  onValueChange,
  value,
}: StepperProps<T>) {
  const { t } = useDynamicLocale();
  if (!Array.isArray(items)) {
    return;
  }

  const currentStep = value ?? defaultActive ?? 1;

  return (
    <div className="space-y-8 text-center pb-3.5 border-b-2 border-b-muted">
      <Root defaultValue={defaultActive} onValueChange={onValueChange} value={value}>
        {items.map(({ step, title, description }: StepItem, index: number) => {
          const isCompleted = step < currentStep;

          return (
            <StepperItem key={step} step={step} className="max-md:items-start [&:not(:first-child)]:flex-1">
              {index > 0 && (
                <StepperSeparator className="max-md:mt-3.5 md:mx-0 md:max-w-[100px]" data-completed={isCompleted} />
              )}

              <StepperTrigger className="max-md:flex-col">
                <StepperIndicator variant="icon" data-completed={isCompleted} />
                <div className="text-center md:text-left">
                  <StepperTitle>{t(title)}</StepperTitle>
                  <StepperDescription className="max-sm:hidden">{t(description)}</StepperDescription>
                </div>
              </StepperTrigger>
            </StepperItem>
          );
        })}
      </Root>
    </div>
  );
}
